import { AuthHeader } from '@/components/shared/AuthHeader'
import { useAuth } from '@/hooks/useAuth'
import { supabase } from '@/lib/supabase'
import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

type Status = 'loading' | 'ready' | 'invalid' | 'success'

export default function ResetPasswordPage() {
  const navigate = useNavigate()
  const { updatePassword } = useAuth()
  const [status, setStatus] = useState<Status>('loading')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    // detectSessionInUrl: true processes the recovery token automatically on client init.
    // Check for an active session; also listen for the PASSWORD_RECOVERY event in case
    // the token is processed after initial mount.
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) setStatus('ready')
      else setStatus('invalid')
    })

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') setStatus('ready')
    })

    return () => subscription.unsubscribe()
  }, [])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)

    if (password !== confirm) {
      setError('Passwords do not match.')
      return
    }

    setSaving(true)
    const { error: err } = await updatePassword(password)
    setSaving(false)

    if (err) {
      setError(err)
      return
    }

    setStatus('success')
    setTimeout(() => navigate('/dashboard', { replace: true }), 2000)
  }

  if (status === 'loading') {
    return (
      <>
        <AuthHeader />
        <main className="flex min-h-screen flex-col items-center justify-center p-8">
          <p className="text-sm text-muted-foreground">Verifying reset link…</p>
        </main>
      </>
    )
  }

  if (status === 'invalid') {
    return (
      <>
        <AuthHeader />
        <main className="flex min-h-screen flex-col items-center justify-center p-8">
          <div className="w-full max-w-sm text-center space-y-4">
            <h1 className="text-2xl font-semibold">Link expired</h1>
            <p className="text-sm text-muted-foreground">
              This reset link is invalid or has expired. Request a new one below.
            </p>
            <Link
              to="/forgot-password"
              className="text-sm text-primary underline underline-offset-4 hover:opacity-80"
            >
              Send a new reset link
            </Link>
          </div>
        </main>
      </>
    )
  }

  if (status === 'success') {
    return (
      <>
        <AuthHeader />
        <main className="flex min-h-screen flex-col items-center justify-center p-8">
          <div className="w-full max-w-sm text-center space-y-4">
            <h1 className="text-2xl font-semibold">Password updated</h1>
            <p className="text-sm text-muted-foreground">
              Your password has been changed. Redirecting you to the dashboard…
            </p>
          </div>
        </main>
      </>
    )
  }

  return (
    <>
      <AuthHeader />
      <main className="flex min-h-screen flex-col items-center justify-center p-8">
        <div className="w-full max-w-sm space-y-6">
          <div className="text-center space-y-1">
            <h1 className="text-2xl font-bold tracking-tight">Choose a new password</h1>
            <p className="text-sm text-muted-foreground">Must be at least 8 characters.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <label htmlFor="password" className="text-sm font-medium">
                New password
              </label>
              <input
                id="password"
                type="password"
                autoComplete="new-password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="At least 8 characters"
              />
            </div>

            <div className="space-y-1.5">
              <label htmlFor="confirm" className="text-sm font-medium">
                Confirm password
              </label>
              <input
                id="confirm"
                type="password"
                autoComplete="new-password"
                required
                minLength={8}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="Repeat your new password"
              />
            </div>

            {error && (
              <p
                role="alert"
                className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive"
              >
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={saving}
              className="w-full rounded-lg bg-primary py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Update password'}
            </button>
          </form>
        </div>
      </main>
    </>
  )
}
